/**
 * The intro's fireworks: three bursts as the steps begin and five at the
 * finale. A seeded random so every run looks the same. Times are seconds
 * from the start of the steps phase.
 */

export interface FireworkRay {
  angle: string;
  className: string;
  color: string;
  delay: string;
  duration: string;
}

export interface FireworkBurst {
  x: string;
  y: string;
  /** When the burst explodes, in seconds. */
  at: number;
  delay: string;
  launch: string;
  glow: string;
  rays: FireworkRay[];
}

const SHELLS = [
  {
    main: "#FF2D55",
    tint: "#FF9AB0",
    glow: "radial-gradient(closest-side, rgba(255,120,150,.9), rgba(255,45,85,0))",
  },
  {
    main: "#FFC23A",
    tint: "#FFF1C2",
    glow: "radial-gradient(closest-side, rgba(255,230,160,.9), rgba(255,194,58,0))",
  },
  {
    main: "#8FA8FF",
    tint: "#E3E9FF",
    glow: "radial-gradient(closest-side, rgba(200,214,255,.9), rgba(143,168,255,0))",
  },
  {
    main: "#FFFFFF",
    tint: "#FFE7B0",
    glow: "radial-gradient(closest-side, rgba(255,255,255,.9), rgba(255,255,255,0))",
  },
];
const GLITTER = ["#FFF4D6", "#FFD36B", "#FFFFFF"];

/** [left %, top %, burst time (s), shell] */
const SPOTS: [number, number, number, number][] = [
  [24, 30, 0.9, 0],
  [77, 26, 1.3, 1],
  [52, 18, 1.7, 3],
  [20, 50, 39.1, 0],
  [80, 46, 39.5, 1],
  [50, 20, 39.9, 2],
  [35, 32, 40.3, 3],
  [66, 30, 40.6, 0],
];

export const makeBursts = (): FireworkBurst[] => {
  let seed = 7;
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };

  return SPOTS.map(([x, y, t, shellIndex]) => {
    const shell = SHELLS[shellIndex];
    const rays: FireworkRay[] = [];
    const count = 34;
    for (let k = 0; k < count; k++) {
      const angle = (360 / count) * k + (rnd() - 0.5) * 9;
      const pick = rnd();
      const size = pick < 0.3 ? " near" : pick > 0.72 ? " far" : "";
      rays.push({
        angle: `${angle.toFixed(1)}deg`,
        className: `pep-spark${size}`,
        color: rnd() < 0.22 ? shell.tint : shell.main,
        delay: `${(t + rnd() * 0.05).toFixed(2)}s`,
        duration: `${(1.6 + rnd() * 0.6).toFixed(2)}s`,
      });
    }
    for (let k = 0; k < 16; k++) {
      rays.push({
        angle: `${(rnd() * 360).toFixed(1)}deg`,
        className: "pep-spark glit",
        color: GLITTER[k % 3],
        delay: `${(t + 0.18 + rnd() * 0.3).toFixed(2)}s`,
        duration: `${(1.2 + rnd() * 0.6).toFixed(2)}s`,
      });
    }
    return {
      x: `${x}%`,
      y: `${y}%`,
      at: t,
      delay: `${t.toFixed(2)}s`,
      launch: `${Math.max(0, t - 0.75).toFixed(2)}s`,
      glow: shell.glow,
      rays,
    };
  });
};
